import type { PlayerRef } from '@remotion/player';
import React, { useCallback, useRef } from 'react';
import { useIsPlaying } from './use-is-playing';
import { useIsBuffering } from './use-is-buffering';
import { useTranslations } from 'next-intl';
import { PauseFill, PlayCircleFill } from '@crabs-ai/web-icons';
import { LoadingSpinner, ToolButton } from '@crabs-ai/ds-react';
import { activateControlKey } from '@waka/compat/control-activation';

export const PlayPauseButton: React.FC<{
  playerRef: React.RefObject<PlayerRef | null>;
}> = ({ playerRef }) => {
  const t = useTranslations();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const playing = useIsPlaying(playerRef);
  const buffering = useIsBuffering(playerRef, 300);

  const onToggle = useCallback(() => {
    playerRef.current?.toggle();
    buttonRef.current?.blur();
  }, [playerRef]);

  const getIcon = () => {
    if (playing && buffering) {
      return <LoadingSpinner className="size-5" />;
    }
    return playing ? <PauseFill /> : <PlayCircleFill />;
  };

  const getTooltip = () => {
    if (playing && buffering) {
      return t('Editor.timeline-controls.pause');
    }
    return playing
      ? t('Editor.timeline-controls.pause')
      : t('Editor.timeline-controls.play');
  };

  return (
    <ToolButton
      ref={buttonRef}
      state="default"
      onClick={onToggle}
      onKeyDown={(event) => {
        activateControlKey(event, onToggle);
      }}
      isShowTooltip={true}
      tooltipNode={getTooltip()}
      tooltipSubNode="[Space]"
      tooltipPosition="bottom"
      size="sm"
      role="button"
      tabIndex={0}
      aria-label={getTooltip()}
      aria-busy={playing && buffering}
    >
      {getIcon()}
      <span className="sr-only">{getTooltip()}</span>
    </ToolButton>
  );
};
